/** @format */
import { TimerOptions } from './TimerOptions';
import { queueDebug } from '../debug';
import { TimerEventEnum } from './Enum/TimerEventEnum';
import TimerEvent from './Event/TimerEvent';
import { TimerManagerSubscribeEventEnum } from './Enum/TimerManagerSubscribeEventEnum';

export class Timer {
    /** JS timeout reference point **/
    protected ref: number | null;

    /** Is timer started? **/
    protected enabled: boolean;

    protected onStart: () => void;
    protected onStop: () => void;
    protected onTick: () => void;
    protected interval: number;
    protected throwRuntimeExceptions: boolean;
    protected identifier: string;
    protected internalData: any;
    protected passInternalDataToEvents: boolean;
    protected dispatchEvents: boolean;
    protected iterationLimitEnabled: boolean;
    protected iterationLimit: number;
    protected iterations: number;

    /**
     * @param options onStart: () => void, onStop: () => void, onTick: () => void, interval: number
     * @param namespace
     * @param {function} [options.onStart] Method executed one time after starting the timer
     * @param {function} [options.onStop] Method executed one time after stopping the timer
     * @param {function} [options.onTick] Method executed every timer tick
     * @param {number} [options.interval] Iteration interval in ms
     * @param {boolean} [options.throwRuntimeExceptions] Should we throw exceptions if start/stop/tick methods failed
     * @param {string} [options.identifier] Timer identifier string
     * @param {any} [options.internalData] Any data to be held by the timer
     * @param {React.ElementRef} [options.parentReference] Parent instance
     * @param {boolean} [options.dispatchEvents] Should we dispatch events?
     */
    constructor(options: TimerOptions = { identifier: 'UNASSIGNED' }, namespace?: string) {
        this.ref = null;
        const {
            onStart = (): void => {
                /**/
            },
            onStop = (): void => {
                /**/
            },
            onTick = (): void => {
                /**/
            },
            interval = 1000,
            throwRuntimeExceptions = false,
            identifier = 'UNASSIGNED',
            internalData = {},
            passInternalDataToEvents = false,
            dispatchEvents = true,
            iterationLimitEnabled = false,
            iterationLimit = -1,
        } = options;

        let errorOcurred = false;
        if (identifier === 'UNASSIGNED') {
            this.emitEvent(TimerEventEnum.timerCreated, { success: false, reason: 'Identifier missing' });
            queueDebug(`Identifier is missing`);
            if (throwRuntimeExceptions) {
                throw Error(`Identifier is missing`);
            }

            errorOcurred = true;
        }
        if (typeof onStart !== 'function') {
            this.emitEvent(TimerEventEnum.timerCreated, { success: false, reason: `${identifier} onStart must be a function` });
            queueDebug(`Timer ${identifier} onStart must be a function`);
            if (throwRuntimeExceptions) {
                throw Error(`onStart ${identifier} property must be a function`);
            }
            errorOcurred = true;
        }
        if (typeof onStop !== 'function') {
            this.emitEvent(TimerEventEnum.timerCreated, { success: false, reason: `${identifier} onStop must be a function` });
            queueDebug(`Timer ${identifier} onStop must be a function`);
            if (throwRuntimeExceptions) {
                throw Error(`onStop ${identifier} property must be a function`);
            }
            errorOcurred = true;
        }
        if (typeof onTick !== 'function') {
            this.emitEvent(TimerEventEnum.timerCreated, { success: false, reason: `${identifier} onTick must be a function` });
            queueDebug(`Timer ${identifier} onTick must be a function`);
            if (throwRuntimeExceptions) {
                throw Error(`onTick ${identifier} property must be a function`);
            }
            errorOcurred = true;
        }
        if (Number.isNaN(interval)) {
            this.emitEvent(TimerEventEnum.timerCreated, { success: false, reason: ` ${identifier} Interval must be a number` });
            queueDebug(`Timer ${identifier} property must be a number`);
            if (throwRuntimeExceptions) {
                throw Error(`interval ${identifier} property must be a number`);
            }
            errorOcurred = true;
        }

        this.onStart = onStart;
        this.onStop = onStop;
        this.onTick = onTick;
        this.interval = interval;
        this.throwRuntimeExceptions = throwRuntimeExceptions;
        this.identifier = identifier ? identifier : '';
        this.internalData = internalData;
        this.passInternalDataToEvents = passInternalDataToEvents;
        this.dispatchEvents = dispatchEvents;
        this.iterationLimitEnabled = iterationLimitEnabled;
        this.iterationLimit = iterationLimit;
        this.iterations = 0;
        this.enabled = false;

        if (errorOcurred) {
            queueDebug(`Timer ${identifier} Could not be created. Removing reference from manager`);
            this.emitEvent(TimerEventEnum.timerCreated, { success: false });
            this.emitEvent(TimerManagerSubscribeEventEnum.managerRemoveReference, { namespace: namespace, identifier: identifier });
            return;
        }

        this.emitEvent(TimerEventEnum.timerCreated, { success: true });
    }

    protected emitEvent = (name: TimerEventEnum | TimerManagerSubscribeEventEnum, detail: {} = {}): void => {
        const data: any = { ...detail, identifier: this.identifier };
        if (this.passInternalDataToEvents) {
            data.internalData = this.internalData;
        }
        queueDebug(`Timer ${this.identifier} dispatching event ${name}`);
        TimerEvent.getInstance().dispatch(
            name,
            {
                detail: data,
            },
            () => {
                /**/
            }
        );
    };

    public getRef = (): number | null => {
        return this.ref;
    };

    public getIdentifier = (): string => {
        return this.identifier;
    };

    public getInternalData = (): any => {
        return this.internalData;
    };

    public setInternalData = (internalData: any): any => {
        this.internalData = internalData;
    };

    public isStarted = (): boolean => {
        return this.enabled;
    };

    public start = (): void | boolean => {
        queueDebug(`Starting timer ${this.identifier}`);
        if (this.enabled) {
            if (this.throwRuntimeExceptions) {
                this.emitEvent(TimerEventEnum.timerStart, { success: false });
                throw Error(`Timer ${this.identifier} has already been started`);
            }

            queueDebug(`Timer ${this.identifier} could not start because it was already running`);
            this.emitEvent(TimerEventEnum.timerStart, { success: false });
            return false;
        }

        this.enabled = true;

        this._onStart().then(() => {
            this.emitEvent(TimerEventEnum.timerStart, { success: true });

            queueDebug(`Timer ${this.identifier} starting first _tick iteration`);
            this.ref = (setTimeout(() => this._tick(), this.interval) as unknown) as number;
        });
    };

    protected _onStart = (): Promise<boolean> => {
        return new Promise((resolve) => {
            this.onStart();
            queueDebug(`Timer ${this.identifier} starting onStartCallback`);
            resolve(true);
        });
    };

    public stop = (): void | boolean => {
        queueDebug(`Stopping timer ${this.identifier}`);

        if (!this.enabled) {
            if (this.throwRuntimeExceptions) {
                this.emitEvent(TimerEventEnum.timerStop, { success: false });
                throw Error(`Timer ${this.identifier} has not been started`);
            }

            queueDebug(`Timer ${this.identifier} could not stop because it was not started in the first place`);
            this.emitEvent(TimerEventEnum.timerStop, { success: false });
            return false;
        }

        this.enabled = false;

        queueDebug(`Timer ${this.identifier} starting onStopCallback`);
        this.onStop();
        this.emitEvent(TimerEventEnum.timerStop, { success: true, refId: this.ref });

        queueDebug(`Timer ${this.identifier} clearing reference`);
        clearTimeout(this.ref!);
        this.ref = null;
    };

    protected _tick = (): void => {
        this._onTick().then(() => {
            if (this.enabled) {
                this.iterations++;
                if (this.iterationLimitEnabled && this.iterationLimit !== -1 && this.iterations >= this.iterationLimit) {
                    this.iterations = 0;
                    this.emitEvent(TimerEventEnum.timerTick, { success: true });
                    this.emitEvent(TimerEventEnum.timerTickLimit, { success: true });
                    queueDebug(`Timer ${this.identifier} reached iteration limit.`);
                    this.stop();
                    return;
                }
                queueDebug(`Timer ${this.identifier} iterating _tick`);
                this.ref = (setTimeout(() => this._tick(), this.interval) as unknown) as number;
            }
        });
    };

    protected _onTick = (): Promise<boolean> => {
        return new Promise((resolve) => {
            this.emitEvent(TimerEventEnum.timerTick, { success: true });
            queueDebug(`Timer ${this.identifier} starting onTickCallback`);
            this.onTick();
            resolve();
        });
    };

    public proxyMethod = (method: string, methodArguments: any | null): any => {
        switch (method) {
            case 'getRef':
                return this.getRef();
            case 'getIdentification':
                return this.getIdentifier();
            case 'getInternalData':
                return this.getInternalData();
            case 'isEnabled':
            case 'isStarted':
                return this.isStarted();
            case 'setInternalData':
                return this.setInternalData(methodArguments);
            default:
                return null;
        }
    };
}
